import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
    ScatterChart, Scatter, CartesianGrid,
    XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Label
} from 'recharts';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    Paper,
    Stack,
    useTheme,
    alpha
} from '@mui/material';

const CHART_PALETTE = [
    '#1E3A8A', '#2563EB', '#3B82F6', '#60A5FA', '#93C5FD',
    '#1E40AF', '#1D4ED8', '#6366F1', '#818CF8', '#A5B4FC'
];

const PIE_COLORS = [
    '#1E3A8A', '#2563EB', '#7C3AED', '#DB2777', '#EA580C',
    '#059669', '#0891B2', '#4F46E5'
];

const formatNumber = (num) => {
    if (num >= 1000000000) return (num / 1000000000).toFixed(2) + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    if (Number.isInteger(num)) return num.toLocaleString();
    return num.toFixed(2);
};

const CustomTooltip = ({ active, payload, label }) => {
    // eslint-disable-next-line react/prop-types
    if (active && payload?.length) {
        return (
            <Box
                sx={{
                    p: 2,
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    background: 'rgba(255, 255, 255, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    minWidth: 160,
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 0.75, fontWeight: 700, color: '#1E293B', fontSize: 13 }}>
                    {label}
                </Typography>
                {payload.map((entry) => (
                    <Box key={entry.name} sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            background: entry.color, mr: 1, flexShrink: 0
                        }} />
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: 12, mr: 1 }}>
                            {entry.name}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: 12 }}>
                            {formatNumber(entry.value)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    }
    return null;
};

const RoundedBar = (props) => {
    const { x, y, width, height, fill } = props;
    const safeHeight = Math.abs(height);
    const safeY = height < 0 ? y + height : y;
    const radius = Math.min(6, width / 2);

    if (safeHeight < radius) {
        return <rect x={x} y={safeY} width={width} height={safeHeight} fill={fill} />;
    }

    return (
        <path
            d={`M${x},${safeY + safeHeight} 
                L${x},${safeY + radius} 
                Q${x},${safeY} ${x + radius},${safeY} 
                L${x + width - radius},${safeY} 
                Q${x + width},${safeY} ${x + width},${safeY + radius} 
                L${x + width},${safeY + safeHeight} Z`}
            fill={fill}
        />
    );
};

const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.04) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
        <text x={x} y={y} fill="#475569" textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central" fontSize={11} fontWeight={500}>
            {name} ({(percent * 100).toFixed(1)}%)
        </text>
    );
};

// Extracted CustomTick component
const CustomTick = (props) => {
    // eslint-disable-next-line react/prop-types
    const { x, y, payload } = props;
    // eslint-disable-next-line react/prop-types
    let displayText = payload.value;
    // eslint-disable-next-line react/prop-types
    if (displayText && displayText.length > 12) {
        // eslint-disable-next-line react/prop-types
        displayText = displayText.substring(0, 10) + '...';
    }
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} textAnchor="end"
                fill="#64748B" fontSize={11} fontWeight={500} transform="rotate(-45)">
                {displayText}
            </text>
        </g>
    );
};

// Extracted CustomYTick component
const CustomYTick = (props) => {
    // eslint-disable-next-line react/prop-types  
    const { x, y, payload } = props;
    const fmt = (num) => {
        if (num >= 1000000000) return (num / 1000000000).toFixed(0) + 'B';
        if (num >= 1000000) return (num / 1000000).toFixed(0) + 'M';
        if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
        return num.toString();
    };
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dx={-8} textAnchor="end"
                fill="#64748B" fontSize={11} fontWeight={500}>
                {/* eslint-disable-next-line react/prop-types */}
                {fmt(payload.value)}
            </text>
        </g>
    );
};

// Extracted LegendFormatter component
const LegendFormatter = (value) => (
    <span style={{ color: '#475569', fontWeight: 500, fontSize: 12 }}>{value}</span>
);

// Map tool names to human-readable labels
const ENDPOINT_LABELS = {
    'get_profile_analytics': 'Profile Analytics',
    'get_post_analytics': 'Post Analytics',
    'get_topic_metrics': 'Topic Metrics',
    'get_topic_messages': 'Topic Messages',
    'trend': 'Trend Analysis',
    'search': 'Search Results',
    'queries': 'Query Data',
};

const formatEndpointLabel = (endpoint) => {
    return ENDPOINT_LABELS[endpoint] || endpoint.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const DynamicBarChart = ({ toolResults }) => {
    const theme = useTheme();
    
    // Get available endpoints from toolResults
    const getAvailableEndpoints = () => {
        // eslint-disable-next-line react/prop-types
        if (!toolResults || toolResults.length === 0) return [];
        // eslint-disable-next-line react/prop-types
        return [...new Set(toolResults.map(result => result?.endpoint).filter(Boolean))];
    };
    
    const availableEndpoints = getAvailableEndpoints();
    const [selectedEndpoint, setSelectedEndpoint] = useState('');
    
    // Filter toolResults by selected endpoint
    const filteredToolResults = useMemo(() => (
        // eslint-disable-next-line react/prop-types
        toolResults?.filter(result => !selectedEndpoint || result?.endpoint === selectedEndpoint) || []
    ), [selectedEndpoint, toolResults]);
    
    const lastDataObject = useMemo(() => (
        filteredToolResults.length > 0 ? filteredToolResults[0] : null
    ), [filteredToolResults]);
    
    const chartData = useMemo(() => {
        if (!lastDataObject?.content) return [];
        const raw = lastDataObject.endpoint === "trend"
            ? lastDataObject.content?.social
            : lastDataObject.content;
        // Normalise: wrap a plain object in an array so charts can process it
        if (Array.isArray(raw)) return raw;
        if (raw && typeof raw === 'object') return [raw];
        return [];
    }, [lastDataObject]);
    const chartSuggestions = useMemo(() => lastDataObject?.chart_suggestions || [], [lastDataObject]);

    const getNumericFields = () => {
        if (!chartData || chartData.length === 0) return [];
        const firstItem = chartData[0];
        if (!firstItem || typeof firstItem !== 'object') return [];
        return Object.keys(firstItem).filter(key => {
            const val = firstItem[key];
            return typeof val === 'number' || (typeof val === 'string' && val !== '' && !Number.isNaN(Number(val)));
        });
    };
    
    const getCategoricalFields = () => {
        if (!chartData || chartData.length === 0) return [];
        const firstItem = chartData[0];
        if (!firstItem || typeof firstItem !== 'object') return [];
        return Object.keys(firstItem).filter(key => {
            const val = firstItem[key];
            return typeof val === 'string' && (val === '' || Number.isNaN(Number(val)));
        });
    };

    const numericFields = getNumericFields();
    const categoricalFields = getCategoricalFields();

    const [selectedYAxis, setSelectedYAxis] = useState('');
    const [selectedXAxis, setSelectedXAxis] = useState('');

    // Check if we have chartable data
    const hasChartableData = numericFields.length > 0 && categoricalFields.length > 0;

    // Set default endpoint when data first loads
    useEffect(() => {
        if (availableEndpoints.length > 0 && !selectedEndpoint) {
            setSelectedEndpoint(availableEndpoints[0]);
        }
    }, [availableEndpoints, selectedEndpoint]);

    // Reset axis selections when the selected endpoint changes
    useEffect(() => {
        if (numericFields.length > 0) {
            setSelectedYAxis(numericFields[0]);
        } else {
            setSelectedYAxis('');
        }
        if (categoricalFields.length > 0) {
            setSelectedXAxis(categoricalFields[0]);
        } else {
            setSelectedXAxis('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedEndpoint]);

    // Get chart type from suggestions based on selected x,y pair
    const currentChartType = useMemo(() => {
        if (chartSuggestions.length > 0) {
            const match = chartSuggestions.find(
                s => s.x_axis === selectedXAxis && s.y_axis === selectedYAxis
            );
            if (match) return match.chart_type;
        }
        return 'bar'; // default fallback
    }, [selectedXAxis, selectedYAxis, chartSuggestions]);

    const getFormattedData = () => {
        if (!selectedXAxis || !selectedYAxis || !chartData || chartData.length === 0) return [];

        const dataMap = new Map();

        chartData.forEach(item => {
            if (!item) return;
            const xValue = item[selectedXAxis];
            if (xValue === undefined || xValue === null) return;

            let yValue = item[selectedYAxis];
            if (typeof yValue === 'string') yValue = Number.parseFloat(yValue.replace(/[^0-9.-]/g, ''));
            if (typeof yValue !== 'number' || Number.isNaN(yValue)) return;

            const xDisplay = String(xValue);
            if (dataMap.has(xDisplay)) {
                const existingItem = dataMap.get(xDisplay);
                existingItem[selectedYAxis] += yValue;
            } else {
                dataMap.set(xDisplay, { [selectedXAxis]: xDisplay, [selectedYAxis]: yValue });
            }
        });

        return Array.from(dataMap.values());
    };

    const formattedData = getFormattedData();

    const gridStroke = alpha(theme.palette.text.secondary, 0.08);
    const axisStroke = alpha(theme.palette.text.secondary, 0.15);

    const commonXAxisProps = {
        dataKey: selectedXAxis,
        angle: -45,
        textAnchor: "end",
        height: 80,
        tick: CustomTick,
        tickLine: false,
        axisLine: { stroke: axisStroke }
    };

    const xAxisLabel = (
        <Label
            value={selectedXAxis.replace(/_/g, ' ').length > 30
                ? selectedXAxis.replace(/_/g, ' ').substring(0, 27) + '...'
                : selectedXAxis.replace(/_/g, ' ')}
            position="insideBottom" offset={-15}
            style={{ textAnchor: 'middle', fill: '#334155', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}
        />
    );

    const commonYAxisProps = {
        tick: CustomYTick,
        tickLine: false,
        axisLine: { stroke: axisStroke }
    };

    const yAxisLabel = (
        <Label
            value={selectedYAxis.replace(/_/g, ' ').length > 30
                ? selectedYAxis.replace(/_/g, ' ').substring(0, 27) + '...'
                : selectedYAxis.replace(/_/g, ' ')}
            angle={-90} position="insideLeft" offset={-10}
            style={{ textAnchor: 'middle', fill: '#334155', fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}
        />
    );

    const gradientDefs = (
        <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563EB" stopOpacity={1} />
                <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0.85} />
            </linearGradient>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.02} />
            </linearGradient>
            <filter id="barShadow" x="-5%" y="-5%" width="110%" height="120%">
                <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#1E3A8A" floodOpacity="0.15" />
            </filter>
        </defs>
    );

    const renderChart = () => {
        if (formattedData.length === 0) return null;

        const chartMargin = { top: 24, right: 32, left: 32, bottom: 80 };

        switch (currentChartType) {
            case 'line':
                return (
                    <LineChart data={formattedData} margin={chartMargin}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Line type="monotone" dataKey={selectedYAxis} stroke="#2563EB"
                            strokeWidth={3} dot={{ r: 5, fill: '#fff', stroke: '#2563EB', strokeWidth: 2.5 }}
                            activeDot={{ r: 7, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}
                            animationEasing="ease-in-out" />
                    </LineChart>
                );

            case 'area':
                return (
                    <AreaChart data={formattedData} margin={chartMargin}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey={selectedYAxis} stroke="#2563EB"
                            fill="url(#areaGradient)" strokeWidth={2.5}
                            dot={{ r: 4, fill: '#fff', stroke: '#2563EB', strokeWidth: 2 }}
                            activeDot={{ r: 6, fill: '#2563EB', stroke: '#fff', strokeWidth: 2 }}
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}
                            animationEasing="ease-in-out" />
                    </AreaChart>
                );

            case 'pie':
                return (
                    <PieChart>
                        <Pie data={formattedData} dataKey={selectedYAxis} nameKey={selectedXAxis}
                            cx="50%" cy="50%" innerRadius={70} outerRadius={160}
                            paddingAngle={3} cornerRadius={6}
                            label={renderPieLabel} labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                            animationDuration={1200} animationEasing="ease-out">
                            {formattedData.map((entry) => (
                                <Cell key={`${entry[selectedXAxis]}-${entry[selectedYAxis]}`} fill={PIE_COLORS[formattedData.indexOf(entry) % PIE_COLORS.length]}
                                    stroke="none" />
                            ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                            wrapperStyle={{ paddingTop: 16, fontSize: 12 }}
                            iconType="circle" iconSize={8}
                            formatter={LegendFormatter}
                        />
                    </PieChart>
                );

            case 'scatter':
                return (
                    <ScatterChart margin={chartMargin}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#CBD5E1', strokeDasharray: '4 4' }} />
                        <Scatter data={formattedData} fill="#2563EB"
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}>
                            {formattedData.map((entry) => (
                                <Cell key={`scatter-${entry[selectedXAxis]}-${entry[selectedYAxis]}`} fill={CHART_PALETTE[formattedData.indexOf(entry) % CHART_PALETTE.length]}
                                    r={7} />
                            ))}
                        </Scatter>
                    </ScatterChart>
                );

            case 'stacked_bar':
            case 'bar':
            default:
                return (
                    <BarChart data={formattedData} margin={chartMargin}
                        barSize={Math.min(40, Math.max(16, 600 / formattedData.length))}>
                        {gradientDefs}
                        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
                        <XAxis {...commonXAxisProps}>{xAxisLabel}</XAxis>
                        <YAxis {...commonYAxisProps}>{yAxisLabel}</YAxis>
                        <Tooltip content={<CustomTooltip />}
                            cursor={{ fill: alpha('#2563EB', 0.04), radius: 4 }} />
                        <Bar dataKey={selectedYAxis} fill="url(#barGradient)"
                            name={selectedYAxis.replace(/_/g, ' ')} animationDuration={1200}
                            animationEasing="ease-out" radius={[6, 6, 0, 0]}
                            shape={<RoundedBar />} isAnimationActive={true} />
                    </BarChart>
                );
        }
    };

    const selectSx = {
        minWidth: 200,
        '& .MuiOutlinedInput-root': {
            borderRadius: '10px',
            background: '#fff',
            fontSize: 13,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#2563EB' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2563EB', borderWidth: 1.5 },
        },
        '& .MuiInputLabel-root': { fontSize: 13, fontWeight: 500 },
        '& .MuiInputLabel-root.Mui-focused': { color: '#2563EB' },
    };

    return (
        <Box>
            {/* Endpoint selector — always visible so the user can switch sources */}
            {availableEndpoints.length > 1 && (
                <Box sx={{ mb: 2 }}>
                    <FormControl size="small" variant="outlined" sx={selectSx}>
                        <InputLabel>Source</InputLabel>
                        <Select value={selectedEndpoint} onChange={(e) => setSelectedEndpoint(e.target.value)} label="Source">
                            {availableEndpoints.map((endpoint) => (
                                <MenuItem key={endpoint} value={endpoint} sx={{ fontSize: 13 }}>
                                    {formatEndpointLabel(endpoint)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            )}

            {/* Empty state message when no chartable data */}
            {!hasChartableData && (
                <Paper
                    elevation={0}
                    sx={{
                        height: 620, width: "100%", p: 4, borderRadius: '16px',
                        background: 'linear-gradient(145deg, #FAFBFF 0%, #F1F5F9 100%)',
                        border: '1px solid rgba(148, 163, 184, 0.15)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
                        <Typography variant="h6" sx={{ mb: 1, color: '#475569', fontWeight: 600 }}>
                            No Chartable Data Available
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                            The selected data source doesn't contain numeric and categorical fields required for visualization.
                            {availableEndpoints.length > 1 && ' Try switching to a different source above.'}
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* Chart controls and visualization */}
            {hasChartableData && (
                <Paper
                    elevation={0}
                    sx={{
                        height: 620, width: "100%", p: 3, borderRadius: '16px',
                        background: 'linear-gradient(145deg, #FAFBFF 0%, #F1F5F9 100%)',
                        overflow: 'hidden',
                        border: '1px solid rgba(148, 163, 184, 0.15)',
                    }}
                >
                    <Stack
                        direction="row" spacing={2}
                        sx={{
                            mb: 3,
                            flexWrap: { xs: 'wrap', md: 'nowrap' },
                            '& > *': { flexGrow: { xs: 1, md: 0 }, mb: { xs: 1.5, md: 0 } }
                        }}
                    >
                        {availableEndpoints.length <= 1 && (
                        <FormControl size="small" variant="outlined" sx={selectSx}>
                            <InputLabel>Source</InputLabel>
                            <Select value={selectedEndpoint} onChange={(e) => setSelectedEndpoint(e.target.value)} label="Source">
                                {availableEndpoints.map((endpoint) => (
                                    <MenuItem key={endpoint} value={endpoint} sx={{ fontSize: 13 }}>
                                        {formatEndpointLabel(endpoint)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        )}

                    <FormControl size="small" variant="outlined" sx={selectSx}>
                        <InputLabel>Y-Axis</InputLabel>
                        <Select value={selectedYAxis} onChange={(e) => setSelectedYAxis(e.target.value)} label="Y-Axis">
                            {numericFields.map((field) => (
                                <MenuItem key={field} value={field} sx={{ fontSize: 13 }}>
                                    {field.replace(/_/g, ' ')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small" variant="outlined" sx={selectSx}>
                        <InputLabel>X-Axis</InputLabel>
                        <Select value={selectedXAxis} onChange={(e) => setSelectedXAxis(e.target.value)} label="X-Axis">
                            {categoricalFields.map((field) => (
                                <MenuItem key={field} value={field} sx={{ fontSize: 13 }}>
                                    {field.replace(/_/g, ' ')}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>

                <Paper
                    elevation={0}
                    sx={{
                        height: 520, width: "100%", p: 2.5, borderRadius: '14px',
                        background: '#FFFFFF',
                        overflow: 'hidden',
                        border: '1px solid rgba(148, 163, 184, 0.1)',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
                    }}
                >
                    {formattedData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            {renderChart()}
                        </ResponsiveContainer>
                    ) : (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100%"
                            sx={{ borderRadius: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16, opacity: 0.3 }}>
                                    <path d="M21 21H4.6C4.03995 21 3.75992 21 3.54601 20.891C3.35785 20.7951 3.20487 20.6422 3.10899 20.454C3 20.2401 3 19.9601 3 19.4V3M7 15V17M11 12V17M15 9V17M19 6V17" stroke="#94A3B8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                                    No data available to display
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Paper>
            </Paper>
            )}
        </Box>
    );
};

// PropTypes for components
CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

RoundedBar.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    fill: PropTypes.string
};

DynamicBarChart.propTypes = {
    toolResults: PropTypes.array
};

// PropTypes for components
CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

RoundedBar.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    fill: PropTypes.string
};

DynamicBarChart.propTypes = {
    toolResults: PropTypes.array
};

export default DynamicBarChart;
